import { useCallback, useEffect, useState } from 'react';
import { adminFetch, apiErrorMessage } from '../adminFetch';

/**
 * Questionnaire de dossier (11/10/2026) : le catalogue commun à la suite.
 *
 * Les questions « cœur » viennent du code (non modifiables ici). On peut
 * AJOUTER des questions — elles remplacent celles qu'on ajoutait au
 * back-office de SEmplyApp — et les désactiver. Une clé ne se renomme jamais :
 * les réponses enregistrées la portent.
 */
const PRODUITS = ['APP', 'COMPTA', 'IMMO', 'PREVI'];
const GROUPES = ['COMMUN', 'APP', 'COMPTA', 'IMMO', 'PREVI'];
const TYPES = ['TEXTE', 'NOMBRE', 'DATE', 'CHOIX', 'BOOLEEN'];

const VIDE = {
  cle: '',
  groupe: 'APP',
  etape: '',
  libelle: '',
  aide: '',
  type: 'BOOLEEN',
  optionsTexte: '',
  conditionsTexte: '',
  fonctionnaliteRequise: '',
  obligatoire: false,
  surchargeAutorisee: false,
  datee: false,
  position: 100,
  active: true,
  correspondances: {},
};

function versFormulaire(q) {
  return {
    ...VIDE,
    ...q,
    aide: q.aide ?? '',
    fonctionnaliteRequise: q.fonctionnaliteRequise ?? '',
    optionsTexte: (q.options ?? []).map((o) => `${o.valeur}|${o.libelle}${o.plan_code ? `|${o.plan_code}` : ''}`).join('\n'),
    conditionsTexte: q.conditions ? JSON.stringify(q.conditions) : '',
  };
}

function versApi(f) {
  const options = f.optionsTexte
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [valeur, libelle, plan_code] = l.split('|').map((x) => x.trim());
      return { valeur, libelle: libelle || valeur, ...(plan_code ? { plan_code } : {}) };
    });
  return {
    cle: f.cle.trim(),
    groupe: f.groupe,
    etape: f.etape.trim(),
    libelle: f.libelle.trim(),
    aide: f.aide.trim() || null,
    type: f.type,
    options: f.type === 'CHOIX' ? options : null,
    conditions: f.conditionsTexte.trim() ? JSON.parse(f.conditionsTexte) : null,
    fonctionnaliteRequise: f.fonctionnaliteRequise.trim() || null,
    obligatoire: f.obligatoire,
    surchargeAutorisee: f.surchargeAutorisee,
    datee: f.datee,
    position: Number(f.position) || 0,
    active: f.active,
    correspondances: Object.fromEntries(Object.entries(f.correspondances).filter(([, v]) => v && v.trim())),
  };
}

export function AdminQuestionsPage() {
  const [questions, setQuestions] = useState(null);
  const [etapes, setEtapes] = useState([]);
  const [edition, setEdition] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [filtre, setFiltre] = useState('');

  const charger = useCallback(async () => {
    const [r1, r2] = await Promise.all([adminFetch('/admin/questions-dossier'), adminFetch('/admin/questions-dossier/etapes')]);
    if (!r1.ok) {
      setErreur(await apiErrorMessage(r1));
      return;
    }
    setQuestions(await r1.json());
    if (r2.ok) setEtapes(await r2.json());
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  async function enregistrer(e) {
    e.preventDefault();
    setErreur(null);
    let corps;
    try {
      corps = versApi(edition);
    } catch {
      setErreur('Conditions : JSON invalide, ex. [{"field":"company_kind","in":["societe"]}]');
      return;
    }
    const r = await adminFetch(edition.id ? `/admin/questions-dossier/${edition.id}` : '/admin/questions-dossier', {
      method: edition.id ? 'PUT' : 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(corps),
    });
    if (!r.ok) {
      setErreur(await apiErrorMessage(r));
      return;
    }
    setEdition(null);
    await charger();
  }

  async function activer(q, active) {
    const r = await adminFetch(`/admin/questions-dossier/${q.id}/active`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ active }),
    });
    if (!r.ok) setErreur(await apiErrorMessage(r));
    await charger();
  }

  if (!questions) return <p>{erreur ?? 'Chargement…'}</p>;

  const visibles = questions.filter(
    (q) => !filtre || q.groupe === filtre || q.correspondances?.[filtre] !== undefined,
  );

  return (
    <div>
      <h1>Questionnaire de dossier</h1>
      <p>
        Questions posées à la création d’un dossier et dans son formulaire global. Les questions « cœur » viennent du
        code ; celles ajoutées ici s’éditent et se désactivent (jamais de suppression : des réponses portent leur clé).
      </p>
      {erreur ? <p className="adm-erreur">{erreur}</p> : null}

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: '1rem 0' }}>
        <label>
          Filtrer :{' '}
          <select value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            <option value="">Toutes</option>
            {GROUPES.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn" style={{ width: 'auto' }} onClick={() => setEdition({ ...VIDE })}>
          Ajouter une question
        </button>
      </div>

      {edition ? (
        <form onSubmit={enregistrer} className="adm-carte" style={{ display: 'grid', gap: '0.6rem', maxWidth: 720, marginBottom: '1.5rem' }}>
          <h2>{edition.id ? `Modifier « ${edition.cle} »` : 'Nouvelle question'}</h2>
          <label>
            Clé (définitive)
            <input value={edition.cle} disabled={Boolean(edition.id)} onChange={(e) => setEdition({ ...edition, cle: e.target.value })} required />
          </label>
          <label>
            Libellé
            <input value={edition.libelle} onChange={(e) => setEdition({ ...edition, libelle: e.target.value })} required />
          </label>
          <label>
            Aide
            <input value={edition.aide} onChange={(e) => setEdition({ ...edition, aide: e.target.value })} />
          </label>
          <label>
            Groupe
            <select value={edition.groupe} onChange={(e) => setEdition({ ...edition, groupe: e.target.value })}>
              {GROUPES.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
          </label>
          <label>
            Étape
            <select value={edition.etape} onChange={(e) => setEdition({ ...edition, etape: e.target.value })} required>
              <option value="">— Choisir —</option>
              {etapes
                .filter((et) => et.groupe === edition.groupe)
                .map((et) => (
                  <option key={et.id} value={et.id}>
                    {et.libelle}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Type
            <select value={edition.type} onChange={(e) => setEdition({ ...edition, type: e.target.value })}>
              {TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          {edition.type === 'CHOIX' ? (
            <label>
              Options (une par ligne : valeur|libellé|plan_code facultatif)
              <textarea rows={4} value={edition.optionsTexte} onChange={(e) => setEdition({ ...edition, optionsTexte: e.target.value })} />
            </label>
          ) : null}
          <label>
            Conditions d’affichage (JSON, facultatif)
            <input
              value={edition.conditionsTexte}
              placeholder='[{"field":"company_kind","in":["societe"]}]'
              onChange={(e) => setEdition({ ...edition, conditionsTexte: e.target.value })}
            />
          </label>
          <label>
            Fonctionnalité requise (clé du catalogue, ex. app.menu.crm — facultatif)
            <input value={edition.fonctionnaliteRequise} onChange={(e) => setEdition({ ...edition, fonctionnaliteRequise: e.target.value })} />
          </label>
          <fieldset>
            <legend>Produits alimentés (nom du champ chez le produit)</legend>
            {PRODUITS.map((p) => (
              <label key={p} style={{ display: 'block' }}>
                {p}{' '}
                <input
                  value={edition.correspondances[p] ?? ''}
                  placeholder="vide = non concerné"
                  onChange={(e) => setEdition({ ...edition, correspondances: { ...edition.correspondances, [p]: e.target.value } })}
                />
              </label>
            ))}
          </fieldset>
          <label>
            <input type="checkbox" checked={edition.obligatoire} onChange={(e) => setEdition({ ...edition, obligatoire: e.target.checked })} /> Obligatoire
          </label>
          <label>
            <input type="checkbox" checked={edition.datee} onChange={(e) => setEdition({ ...edition, datee: e.target.checked })} /> Valeur datée (date d’effet)
          </label>
          <label>
            <input
              type="checkbox"
              checked={edition.surchargeAutorisee}
              onChange={(e) => setEdition({ ...edition, surchargeAutorisee: e.target.checked })}
            />{' '}
            Valeur propre à un produit autorisée
          </label>
          <label>
            Position
            <input type="number" value={edition.position} onChange={(e) => setEdition({ ...edition, position: e.target.value })} />
          </label>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="submit" className="btn" style={{ width: 'auto' }}>
              Enregistrer
            </button>
            <button type="button" className="btn btn-secondary" style={{ width: 'auto' }} onClick={() => setEdition(null)}>
              Annuler
            </button>
          </div>
        </form>
      ) : null}

      <table className="adm-table">
        <thead>
          <tr>
            <th>Clé</th>
            <th>Groupe / étape</th>
            <th>Libellé</th>
            <th>Type</th>
            <th>Produits</th>
            <th>Origine</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {visibles.map((q) => (
            <tr key={q.id} style={q.active ? undefined : { opacity: 0.5 }}>
              <td>
                <code>{q.cle}</code>
              </td>
              <td>
                {q.groupe} / {q.etape}
              </td>
              <td>{q.libelle}</td>
              <td>
                {q.type}
                {q.datee ? ' · datée' : ''}
                {q.obligatoire ? ' · obligatoire' : ''}
              </td>
              <td>
                {Object.entries(q.correspondances)
                  .map(([p, c]) => `${p}:${c}`)
                  .join(', ')}
              </td>
              <td>{q.coeur ? 'Cœur' : 'Ajoutée'}</td>
              <td>
                {!q.coeur ? (
                  <>
                    <button type="button" className="adm-link" onClick={() => setEdition(versFormulaire(q))}>
                      Modifier
                    </button>{' '}
                    <button type="button" className="adm-link" onClick={() => void activer(q, !q.active)}>
                      {q.active ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
